import { ApiEndpoints } from "@shared/api/api-endpoints";
import { mxApiClient } from "@shared/api/clients/apiClientFactory";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";
import { transformJsonToObject } from "@shared/utils/jsonToObjectTransformer";
import {
  ActiveReadCycles,
  AuditUserMapResult,
  ExemptionsReadingResponse,
  MeterCount,
  MeterCycleAllocation,
  MeterReadingConsumption,
  MeterReadingDetail,
  MissingReadingsResponse,
  NewReadingsList,
  PreviousReadingImage,
  ReadingSummary,
  ReasonCodeItem,
  RouteItem,
  SOPList,
  ValidationCode,
  ValidationRules,
} from "./types";

const getValidationBreakRunCount = async (
  meterRunId: string,
  page: number,
  limit: number,
  remoteUtilityId: string
): Promise<MeterCount> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "mx",
    "validation-break-run-count",
    (query) => {
      query.push("remote_utility_id", remoteUtilityId);
      query.push("page", String(page));
      query.push("limit", String(limit));
      query.push("meter_run_id", meterRunId);
    }
  );

  const response = await mxApiClient.get(url);
  const transformedResponse = transformJsonToObject<MeterCount>(
    response.data.result
  );
  return transformedResponse;
};

const getNewMeterReadings = async ({
  remoteUtilityId,
  meterRunId,
  page,
  limit,
  searchData = "",
  premises,
  sortBy,
}: {
  remoteUtilityId: string;
  meterRunId: string;
  page: number;
  limit: number;
  searchData?: string;
  premises?: string[];
  sortBy?: string[];
}): Promise<NewReadingsList> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "mx",
    "new-meter-readings",
    (query) => {
      query.push("remote_utility_id", remoteUtilityId);
      query.push("meter_run_id", String(meterRunId));
      query.push("page", String(page));
      query.push("limit", String(limit));
      query.push("search_data", searchData);

      if (premises) {
        premises.forEach((premise) => {
          query.push("premise", premise);
        });
      }
      if (sortBy) {
        sortBy.forEach((order) => {
          query.push("order_by", order);
        });
      }
    }
  );

  const response = await mxApiClient.get(url);
  return transformJsonToObject<NewReadingsList>(response.data);
};

const getRoutesList = async (cycleId: number): Promise<RouteItem[]> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "mx",
    "routes-list",
    (query) => {
      query.push("cycle_id", String(cycleId));
    }
  );

  const response = await mxApiClient.get(url);
  const transformedResponse = transformJsonToObject<RouteItem[]>(
    response.data.result
  );
  return transformedResponse;
};

const getReadings = async ({
  remoteUtilityId,
  meterRunId,
  page,
  limit,
  ruleStatus,
  searchData,
  premises,
  sortBy,
}: {
  remoteUtilityId: string;
  meterRunId: string;
  page: number;
  limit: number;
  ruleStatus?: string;
  searchData?: string;
  premises?: string[];
  sortBy?: string[];
}): Promise<any> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "mx",
    "view-readings-list",
    (query) => {
      query.push("remote_utility_id", remoteUtilityId);
      query.push("meter_run_id", String(meterRunId));
      query.push("page", String(page));
      query.push("limit", String(limit));

      if (ruleStatus) {
        query.push("rule_status", ruleStatus);
      }

      if (searchData) {
        query.push("search_data", searchData);
      }

      if (premises) {
        premises.forEach((premise) => {
          query.push("premise", premise);
        });
      }

      if (sortBy) {
        sortBy.forEach((order) => {
          query.push("order_by", order);
        });
      }
    }
  );

  const response = await mxApiClient.get(url);
  return transformJsonToObject<any>(response.data);
};

export const exportMissingReadingsList = async ({
  remoteUtilityId,
  meterRunId,
  page,
  searchData,
  premises,
  sortBy,
}: {
  remoteUtilityId: string;
  meterRunId: string;
  page?: number;
  searchData?: string;
  premises?: string[];
  sortBy?: string[];
}): Promise<any> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "mx",
    "missing-readings-list",
    (query) => {
      query.push("remote_utility_id", remoteUtilityId);
      query.push("meter_run_id", meterRunId);
      if (page) query.push("page", String(page));

      if (searchData) {
        query.push("search_data", searchData);
      }

      if (premises) {
        premises.forEach((premise) => {
          query.push("premise", premise);
        });
      }

      if (sortBy) {
        sortBy.forEach((order) => {
          query.push("order_by", order);
        });
      }

      query.push("export_format", "csv");
    }
  );

  const response = await mxApiClient.get(url);
  return response.data;
};

const getMissingReadingsList = async ({
  remoteUtilityId,
  meterRunId,
  page,
  limit,
  searchData,
  premises,
  sortBy,
}: {
  remoteUtilityId: string;
  meterRunId: string;
  page: number;
  limit: number;
  searchData?: string;
  premises?: string[];
  sortBy?: string[];
}): Promise<MissingReadingsResponse> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "mx",
    "missing-readings-list",
    (query) => {
      query.push("remote_utility_id", remoteUtilityId);
      query.push("meter_run_id", meterRunId);
      query.push("page", String(page));
      query.push("limit", String(limit));

      if (searchData) {
        query.push("search_data", searchData);
      }

      if (premises) {
        premises.forEach((premise) => {
          query.push("premise", premise);
        });
      }

      if (sortBy) {
        sortBy.forEach((order) => {
          query.push("order_by", order);
        });
      }
    }
  );

  const response = await mxApiClient.get(url);
  return transformJsonToObject<MissingReadingsResponse>(response.data);
};

const getExemptionsReadingList = async ({
  meterRunId,
  page,
  limit,
  remoteUtilityId,
  searchData,
  premises,
  sortBy,
}: {
  meterRunId: string;
  page: number;
  limit: number;
  remoteUtilityId: string;
  searchData?: string;
  premises?: string[];
  sortBy?: string[];
}): Promise<ExemptionsReadingResponse> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "mx",
    "validation-break",
    (query) => {
      query.push("remote_utility_id", remoteUtilityId);
      query.push("meter_run_id", meterRunId);
      query.push("page", String(page));
      query.push("limit", String(limit));
      query.push("detail_parameter", "Exemptions");

      if (searchData) {
        query.push("search_data", searchData);
      }

      if (premises) {
        premises.forEach((premise) => {
          query.push("premise", premise);
        });
      }

      if (sortBy) {
        sortBy.forEach((order) => {
          query.push("order_by", order);
        });
      }
    }
  );

  const response = await mxApiClient.get(url);
  return transformJsonToObject<ExemptionsReadingResponse>(response.data);
};

export const getMeterReadingDetail = async (
  remoteUtilityId: string,
  meterRunId: string,
  meterReadingId: string,
  validatorId: any
): Promise<MeterReadingDetail> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "mx",
    "meter-reading-detail",
    (query) => {
      query.push("remote_utility_id", remoteUtilityId);
      query.push("meter_run_id", meterRunId);
      query.push("meter_reading_id", meterReadingId);
      validatorId.forEach((validator) => {
        query.push("remote_validator_id", validator);
      });
    }
  );

  const response = await mxApiClient.get(url);
  return transformJsonToObject<MeterReadingDetail>(response.data.result);
};

const getMeterReadingConsumption = async (
  remoteUtilityId: string,
  meterNumber: string
): Promise<MeterReadingConsumption> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "mx",
    "meter-reading-consumption",
    (query) => {
      query.push("remote_utility_id", remoteUtilityId);
      query.push("meter_number", meterNumber);
    }
  );

  const response = await mxApiClient.get(url);
  return transformJsonToObject<MeterReadingConsumption>(response.data.result);
};

const getPreviousImage = async (
  remoteUtilityId: string,
  readingId: string
): Promise<PreviousReadingImage> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "mx",
    "get-image",
    (query) => {
      query.push("remote_utility_id", remoteUtilityId);
      query.push("meter_reading_id", readingId);
    }
  );

  const response = await mxApiClient.get(url);
  return transformJsonToObject<PreviousReadingImage>(response.data.result);
};

export const downloadExcelSheet = async ({
  remoteUtilityId,
  meterRunId,
  fileName,
  ruleStatus,
  statusFilter,
}: {
  remoteUtilityId: string;
  meterRunId: string;
  fileName: string;
  ruleStatus?: string;
  statusFilter?: string[];
}) => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "mx",
    "download-excel",
    (query) => {
      query.push("remote_utility_id", remoteUtilityId);
      query.push("meter_run_id", meterRunId);
      query.push("file_name", fileName);
      if (ruleStatus) {
        query.push("rule_status", ruleStatus);
      }
      if (statusFilter) {
        statusFilter.forEach((status) => {
          query.push("status_filter", String(status));
        });
      }
    }
  );

  const response = await mxApiClient.get(url);
  return response;
};
const getValidationCodes = async (
  remoteUtilityId: string
): Promise<ValidationCode[]> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "mx",
    "validation-code",
    (query) => {
      query.push("remote_utility_id", remoteUtilityId);
    }
  );

  const response = await mxApiClient.get(url);
  return transformJsonToObject<ValidationCode[]>(response.data.result);
};

export const updateMeterReadingAdjustment = async (
  meterReadingId: string,
  payload: any
): Promise<void> => {
  const url = ApiEndpoints.createUrlWithPathVariables(
    "mx",
    "meter-reading-adjustment",
    [meterReadingId]
  );

  await mxApiClient.put(url, payload);
};

const updatePassExemptReading = async (
  meterReadingId: string,
  payload: any
): Promise<void> => {
  const url = ApiEndpoints.createUrlWithPathVariables(
    "mx",
    "meter-reading-check",
    [meterReadingId]
  );

  await mxApiClient.put(url, payload);
};

const getReasonCodeList = async (
  remoteUtilityId: string,
  codeId: any
): Promise<ReasonCodeItem[]> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "mx",
    "reason-code",
    (query) => {
      query.push("remote_utility_id", remoteUtilityId);
      query.push("code_id", codeId);
    }
  );

  const response = await mxApiClient.get(url);
  const transformedResponse = transformJsonToObject<ReasonCodeItem[]>(
    response.data.result
  );
  return transformedResponse;
};

const putMeterReadingRevisit = async (
  meterReadingId: string,
  payload: any
): Promise<any> => {
  const url = ApiEndpoints.createUrlWithPathVariables(
    "mx",
    "meter-reading-revisit",
    [meterReadingId]
  );

  const response = await mxApiClient.put(url, payload);
};

export const putMeter = async ({
  meterId,
  payload,
}: {
  meterId: number;
  payload: any;
}) => {
  const url = ApiEndpoints.createUrlWithPathVariables("mx", "meter", [meterId]);
  const response = await mxApiClient.put(url, payload);
  return response.data;
};

const getAllSOPs = async ({
  remoteUtilityId,
}: {
  remoteUtilityId: string;
}): Promise<SOPList[]> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "mx",
    "get-all-sops",
    (query) => {
      query.push("remote_utility_id", remoteUtilityId);
      query.push("association_type", "METER");
      query.push("is_active", "true");
      query.push("operation_type", 1);
    }
  );

  const response = await mxApiClient.get(url);
  return transformJsonToObject<SOPList[]>(response.data.result);
};

export const getValidationBreakSummary = async (
  remoteUtilityId: string
): Promise<ReadingSummary> => {
  const url = ApiEndpoints.createUrlWithPathAndQueryVariables(
    "mx",
    "validation-break",
    ["summary"],
    (query) => {
      query.push("remote_utility_id", remoteUtilityId);
      query.push("moth_wise_data", "0");
    }
  );

  const response = await mxApiClient.get(url);
  const transformedResponse = transformJsonToObject<ReadingSummary>(
    response.data.result
  );
  return transformedResponse;
};

export const getActiveCyclesList = async (
  remoteUtilityId: string,
  page: number,
  limit: number,
  filters?: Record<string, any>,
  search?: string,
  isCompleted?: boolean
): Promise<ActiveReadCycles> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "mx",
    "validation-break",
    (query) => {
      query.push("remote_utility_id", remoteUtilityId);
      query.push("page", page);
      query.push("limit", limit);
      query.push("include_validator_info",1); //flag needed by backend
      if (search) query.push("search_data", search);
      query.push("is_completed", isCompleted);

      // ✅ Dynamically append filters if present
      if (filters) {
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== "")
            query.push(key, value);
        });
      }
    }
  );

  const response = await mxApiClient.get(url);
  return transformJsonToObject<ActiveReadCycles>(response.data);
};


const getValidationRules = async (
  remoteUtilityId: string
): Promise<ValidationRules[]> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "mx",
    "utility-rule",
    (query) => {
      query.push("remote_utility_id", remoteUtilityId);
    }
  );

  const response = await mxApiClient.get(url);
  return transformJsonToObject<ValidationRules[]>(response.data.result);
};

export const putValidationRule = async ({
  id,
  payload,
}: {
  id: string;
  payload: any;
}) => {
  const url = ApiEndpoints.createUrlWithPathVariables("mx", "utility-rule", [
    id,
  ]);

  const response = await mxApiClient.put(url, payload);
  return response?.data;
};

export const getMeterCycleAuditUserMapping = async (
  remoteUtilityId: string,
  search?: string
): Promise<MeterCycleAllocation[]> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "mx",
    "meter-cycles/audit-user-mapping",
    (query) => {
      query.push("remote_utility_id", remoteUtilityId);
      if (search) {
        query.push("search_data", search);
      }
    }
  );

  const response = await mxApiClient.get(url);
  return transformJsonToObject<MeterCycleAllocation[]>(response.data.result);
};

const getAuditUsers = async (
  remoteUtilityId: string,
  userType: number,
  cycleId?: number,
  search?: string
): Promise<AuditUserMapResult[]> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "mx",
    "audit-user",
    (query) => {
      query.push("remote_utility_id", remoteUtilityId);
      query.push("user_type", String(userType));
      if (cycleId) {query.push("cycle_id", cycleId);}
      if (search) {query.push("search", search);}
    }
  );

  const response = await mxApiClient.get(url);
  return transformJsonToObject<AuditUserMapResult[]>(response.data.result);
};

const postMeterCycleAuditUserMapping = async (payload: any) => {
  const url = ApiEndpoints.createUrl("mx", "meter-cycles/audit-user-mapping");

  const response = await mxApiClient.post(url, payload);
  return response?.data;
};

const putMeterCycleAuditUserMapping = async (payload: any) => {
  const url = ApiEndpoints.createUrl("mx", "meter-cycles/audit-user-mapping");

  const response = await mxApiClient.put(url, payload);
  return response?.data;
};

const putValidationCode = async (
  id: number,
  remoteUtilityId: string,
  payload: Partial<ValidationCode>
) => {
  const url = ApiEndpoints.createUrlWithPathAndQueryVariables(
    "mx",
    "validation-code",
    [id],
    (query) => {
      query.push("remote_utility_id", remoteUtilityId);
    }
  );

  const response = await mxApiClient.put(url, payload);
  return response?.data;
};

const postValidationCode = async (payload: Partial<ValidationCode>) => {
  const url = ApiEndpoints.createUrl("mx", "validation-code");

  const response = await mxApiClient.post(url, payload);
  return response?.data;
};

export const deleteValidationCode = async (
  id: number,
  remoteUtilityId: string
) => {
  const url = ApiEndpoints.createUrlWithPathAndQueryVariables(
    "mx",
    "validation-code",
    [id],
    (query) => {
      query.push("remote_utility_id", remoteUtilityId);
    }
  );

  const response = await mxApiClient.delete(url);
  return response?.data;
};

const getRoutes = async (
  queryParams: Record<string, any> = {},
  cycleIds: number[]
): Promise<any> => {
  const remoteUtilityId = getRemoteUtilityId();
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "mx",
    "meter-dispatch/detail",
    (query) => {
      query.push("remote_utility_id", remoteUtilityId);
      query.push("origin", "Web");

      cycleIds.forEach((id) => query.push("cycle_id", id));
      Object.entries(queryParams).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          if (Array.isArray(value)) {
            value.forEach((v) => {
              if (v !== undefined && v !== null && v !== "") {
                query.push(key, v.toString());
              }
            });
          } else {
            query.push(key, value.toString());
          }
        }
      });
    }
  );

  const response = await mxApiClient.get(url);
  return response.data;
};

const getMeterReaders = async (
  queryParams: Record<string, any> = {}
): Promise<any> => {
  const remoteUtilityId = getRemoteUtilityId();
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "mx",
    "meter-reader",
    (query) => {
      query.push("remote_utility_id", remoteUtilityId);
      query.push("is_active", "True");
      Object.entries(queryParams).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          if (Array.isArray(value)) {
            value.forEach((v) => {
              if (v !== undefined && v !== null && v !== "") {
                query.push(key, v.toString());
              }
            });
          } else {
            query.push(key, value.toString());
          }
        }
      });
    }
  );

  const response = await mxApiClient.get(url);
  return response.data;
};

const assignMeterReader = async (
  routeId: number,
  readerId: number[]
): Promise<any> => {
  const url = ApiEndpoints.createUrlWithPathVariables(
    "mx",
    "meter-dispatch/detail",
    [routeId]
  );

  const response = await mxApiClient.patch(url, {
    is_assigned: true,
    reader_id: readerId,
  });
  return response.data;
};

const getDuplicateReadings = async ({
  remoteUtilityId,
  meterRunId,
  page,
  limit,
  searchData = "",
  premises,
  sortBy,
}: {
  remoteUtilityId: string;
  meterRunId: string;
  page: number;
  limit: number;
  searchData?: string;
  premises?: string[];
  sortBy?: string[];
}): Promise<NewReadingsList> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "mx",
    "duplicate-readings-list",
    (query) => {
      query.push("remote_utility_id", remoteUtilityId);
      query.push("meter_run_id", String(meterRunId));
      query.push("page", String(page));
      query.push("limit", String(limit));
      if (searchData !== null && searchData !== "") {
        query.push("search_data", searchData);
      }

      if (premises) {
        premises.forEach((premise) => {
          query.push("premise", premise);
        });
      }
      if (sortBy) {
        sortBy.forEach((order) => {
          query.push("order_by", order);
        });
      }
    }
  );

  const response = await mxApiClient.get(url);
  return transformJsonToObject<NewReadingsList>(response.data);
};

const replaceDuplicateReading = async (payload: any): Promise<void> => {
  const url = ApiEndpoints.createUrlWithPathVariables(
    "mx",
    "replace-duplicate-readings"
  );

  await mxApiClient.patch(url, payload);
};

const getRoutesListForNavigation = async ({
  remoteUtilityId,
  page,
  limit,
  isBaseInfo = true,
  isPaginationRequired = false,
}: {
  remoteUtilityId: string;
  page: number;
  limit: number;
  isBaseInfo?: boolean;
  isPaginationRequired?: boolean;
}): Promise<any> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "mx",
    "route",
    (query) => {
      query.push("remote_utility_id", remoteUtilityId);
      query.push("page", page);
      query.push("limit", limit);

      // optional flags
      if (isBaseInfo) query.push("is_base_info", 1);
      if (isPaginationRequired) query.push("is_pagination_required", isPaginationRequired);
    }
  );

  const response = await mxApiClient.get(url);
  return response.data;
};

const putEstimateReadings = async (
  meterRunId: string,
  payload: any
): Promise<any> => {
  const url = ApiEndpoints.createUrlWithPathVariables(
    "mx",
    "meter-run",
    [meterRunId, 'estimate']
  );

  const response = await mxApiClient.post(url, payload);
};

// Add your feature-specific API calls here
export const mxValidationsApi = {
  getValidationBreakRunCount,
  getNewMeterReadings,
  getRoutesList,
  getReadings,
  getMissingReadingsList,
  getExemptionsReadingList,
  getMeterReadingDetail,
  getMeterReadingConsumption,
  getPreviousImage,
  getValidationCodes,
  updateMeterReadingAdjustment,
  getReasonCodeList,
  putMeterReadingRevisit,
  putMeter,
  getAllSOPs,
  updatePassExemptReading,
  getValidationBreakSummary,
  getActiveCyclesList,
  getValidationRules,
  putValidationRule,
  getMeterCycleAuditUserMapping,
  getAuditUsers,
  postMeterCycleAuditUserMapping,
  putMeterCycleAuditUserMapping,
  putValidationCode,
  postValidationCode,
  deleteValidationCode,
  getRoutes,
  getMeterReaders,
  assignMeterReader,
  getDuplicateReadings,
  replaceDuplicateReading,
  getRoutesListForNavigation,
  putEstimateReadings,
  exportMissingReadingsList
};
